import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './HomeHeroCarousel.css';

import OptimizedImage from './OptimizedImage';
import ContactModal from './ContactModal';
import ReportModal from './ReportModal';
import { slides } from './homeHeroSlides';

const ArrowIcon = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path d="M13.6946 30.1437L29.6781 14.1602" stroke="#171717" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M30.1403 30.4558L30.1403 13.7005L12.3723 13.7004" stroke="#171717" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>

);

const HpcArrowLeftIcon = () => (
  <svg viewBox="0 0 801 1493" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path d="M725.275 33.9177L33.918 725.275L767.061 1458.42" stroke="white" strokeWidth="67.8355" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const HpcArrowRightIcon = () => (
  <svg viewBox="0 0 801 1493" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path d="M75.7031 1458.42L767.061 767.065L33.9177 33.9177" stroke="white" strokeWidth="67.8355" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Rounded-rectangle badge outline with a notch carved out of the top-right
// corner so the floating icon nests into the glass shape.
const generateBadgePath = (width, height) => {
  const w = width;
  const h = height;
  return `M${w - 28} 47.0008C${w - 39.046} 47.0009 ${w - 48} 38.0466 ${w - 48} 27.0008V20.5004C${
    w - 48
  } 9.45477 ${w - 56.954} 0.500471 ${
    w - 68
  } 0.500451L20.5 0.500038C9.45432 0.500017 0.5 9.45433 0.5 20.5V${h - 20}C0.5 ${
    h - 8.954
  } 9.45798 ${h} 20.5037 ${h}L${w - 20.004} ${h}C${w - 8.958} ${h} ${w} ${h - 8.954} ${w} ${
    h - 20
  }V67.0007C${w} 55.9549 ${w - 8.954} 47.0006 ${w - 20} 47.0007L${w - 28} 47.0008Z`;
};

// Glass stat badge: a backdrop-filter blurs whatever is actually rendered
// behind the badge, clipped to the notched path; a second SVG strokes the
// border along the same path so the notch reads as part of the frame.
const HpcBadge = ({ badge, icon }) => {
  const containerRef = React.useRef(null);
  const [dimensions, setDimensions] = React.useState({ width: 308, height: 144 });

  React.useLayoutEffect(() => {
    if (!containerRef.current) return undefined;

    const updateSize = () => {
      const { offsetWidth, offsetHeight } = containerRef.current;
      if (offsetWidth > 0 && offsetHeight > 0) {
        setDimensions({ width: offsetWidth, height: offsetHeight });
      }
    };

    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(containerRef.current);
    window.addEventListener('resize', updateSize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  const pathD = generateBadgePath(dimensions.width, dimensions.height);

  return (
    <div className='hpc-badge' ref={containerRef}>
      {/* Real backdrop blur of the photo behind, clipped to the notch. */}
      <div
        className='hpc-badge-glass'
        style={{ clipPath: `path('${pathD}')`, WebkitClipPath: `path('${pathD}')` }}
      />

      <svg
        className='hpc-badge-border-svg'
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        preserveAspectRatio='none'
      >
        <path d={pathD} />
      </svg>

      <div className='hpc-badge-content'>
        <span className='hpc-badge-label'>{badge.label}</span>
        <span className='hpc-badge-value'>{badge.value}</span>
      </div>

      <div className='hpc-badge-icon'>{icon}</div>
    </div>
  );
};

const HpcSlide = ({ slide, isActive, leavingClass, onAnimationEnd, onCtaClick }) => {
  return (
    <div
      className={`hpc-slide ${isActive ? 'active' : ''} ${leavingClass}`}
      aria-hidden={!isActive}
      onAnimationEnd={onAnimationEnd}
    >
      {/* 1. Full-bleed background. A separate, tighter crop is served on
             phones via media-scoped <source>s ahead of the desktop ones. */}
      <picture>
        {slide.bgMobile && (
          <>
            <source media='(max-width: 768px)' srcSet={slide.bgMobile.avif} type='image/avif' />
            <source media='(max-width: 768px)' srcSet={slide.bgMobile.webp} type='image/webp' />
            <source media='(max-width: 768px)' srcSet={slide.bgMobile.fallback} />
          </>
        )}
        <source srcSet={slide.bg.avif} type='image/avif' />
        <source srcSet={slide.bg.webp} type='image/webp' />
        <img src={slide.bg.fallback} alt='' className='hpc-bg' />
      </picture>

      {/* Darkening overlay over the photo (behind the panels/figure). */}
      {slide.overlayBg && (
        <div className='hpc-overlay' style={{ background: slide.overlayBg }} aria-hidden='true' />
      )}

      {/* 2. Bordered panels overlaid on the image */}
      <div className='hpc-panels'>
        <div className='hpc-panel hpc-panel--main'>
          <HpcBadge badge={slide.badge} icon={slide.badge.icon} />
          <h1 className='hpc-title'>{slide.title}</h1>
        </div>

        <div
          className='hpc-panel hpc-panel--side custom_click_track'
          role='link'
          tabIndex={0}
          style={{ '--hpc-side-bg': slide.sideBg || 'none' }}
          data-custom_click_track_value={`Hero|${slide.cta.label}|Internal|`}
          onClick={() => onCtaClick(slide.cta)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onCtaClick(slide.cta);
            }
          }}
        >
          <p className='hpc-side-text'>{slide.sideText}</p>
          <div className='hpc-cta'>
            <span className='hpc-cta-label'>{slide.cta.label}</span>
            <span className='hpc-cta-arrow'>
              <ArrowIcon />
            </span>
          </div>
        </div>
      </div>

      {/* 3. Cut-out figure overlaid ON TOP of the borders so the
             subject appears to break through the frames. */}
      {slide.overlay && (
        <OptimizedImage
          avif={slide.overlay.avif}
          webp={slide.overlay.webp}
          fallback={slide.overlay.fallback}
          alt=''
          className='hpc-figure'
        />
      )}
    </div>
  );
};

const HomeHeroCarousel = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  // The slide currently sliding away on top, plus the direction it travels.
  const [leaving, setLeaving] = useState(null); // { index, dir: 'next' | 'prev' }
  const hasMultiple = slides.length > 1;

  // Change slides with a direction so the outgoing card sweeps the right way.
  const goToSlide = (index, dir) => {
    const next = (index + slides.length) % slides.length;
    setCurrentSlide((prev) => {
      if (next !== prev) setLeaving({ index: prev, dir });
      return next;
    });
  };

  const goNext = () => goToSlide(currentSlide + 1, 'next');
  const goPrev = () => goToSlide(currentSlide - 1, 'prev');

  const handleAnimationEnd = (idx) => {
    setLeaving((cur) => (cur && cur.index === idx ? null : cur));
  };

  // Touch-swipe across the whole hero: track the start point and, on release,
  // advance/rewind if the gesture was a clear horizontal swipe.
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const didSwipe = useRef(false);

  const handleTouchStart = (e) => {
    const t = e.touches[0];
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
    didSwipe.current = false;
  };

  const handleTouchEnd = (e) => {
    if (!hasMultiple) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - touchStartY.current;
    // Require a clear, mostly-horizontal movement so vertical scrolls and taps
    // aren't misread as swipes.
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      didSwipe.current = true;
      if (dx < 0) goNext();
      else goPrev();
    }
  };

  // Suppress the click that the browser synthesizes at the end of a touch so a
  // swipe ending on the CTA panel doesn't also trigger its link.
  const handleClickCapture = (e) => {
    if (didSwipe.current) {
      e.preventDefault();
      e.stopPropagation();
      didSwipe.current = false;
    }
  };

  const handleCta = (cta) => {
    if (!cta) return;
    if (cta.action === 'contact') {
      navigate('/contact');
      return;
    }
    if (cta.action === 'report') {
      setIsReportOpen(true);
      return;
    }
    const { href } = cta;
    // Ignore placeholder links until a real destination is provided.
    if (!href || href === '#') return;
    // Internal hash routes are handled by react-router; keep external links native.
    if (href.startsWith('/#/')) {
      navigate(href.replace('/#', ''));
    } else {
      window.location.href = href;
    }
  };

  return (
    <section className='hpc-hero'>
      <div className='sr-only' aria-live='polite'>
        {hasMultiple &&
          `Slide ${currentSlide + 1} of ${slides.length}: ${slides[currentSlide]?.title || ''}`}
      </div>
      <div
          className='hpc-stage'
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onClickCapture={handleClickCapture}
        >
        {slides.map((slide, index) => {
          const isLeaving = leaving && leaving.index === index;
          const leavingClass = isLeaving
            ? leaving.dir === 'next'
              ? 'leaving-next'
              : 'leaving-prev'
            : '';
          return (
            <HpcSlide
              key={slide.id}
              slide={slide}
              isActive={index === currentSlide}
              leavingClass={leavingClass}
              onAnimationEnd={() => handleAnimationEnd(index)}
              onCtaClick={handleCta}
            />
          );
        })}

        {hasMultiple && (
          <>
            <button
              type='button'
              className='hpc-arrow hpc-arrow--prev'
              aria-label='Previous slide'
              onClick={goPrev}
            >
              <HpcArrowLeftIcon />
            </button>
            <button
              type='button'
              className='hpc-arrow hpc-arrow--next'
              aria-label='Next slide'
              onClick={goNext}
            >
              <HpcArrowRightIcon />
            </button>

            <div className='hpc-controls-row'>
              <div className='hpc-dots'>
                {slides.map((slide, index) => (
                  <button
                    key={slide.id}
                    type='button'
                    aria-label={`Go to slide ${index + 1}`}
                    className={`hpc-dot ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => goToSlide(index, index >= currentSlide ? 'next' : 'prev')}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
      <ReportModal isOpen={isReportOpen} onClose={() => setIsReportOpen(false)} />
    </section>
  );
};

export default HomeHeroCarousel;
